<template>
  <Teleport to="body">
    <div v-if="!dismissed" class="update-overlay">
      <div class="update-overlay__backdrop" />
      <div
        class="update-overlay__dialog"
        role="alertdialog"
        aria-modal="true"
        :aria-label="t('app.update_available')"
      >
        <h2 class="update-overlay__title">{{ t('app.update_available') }}</h2>
        <p class="update-overlay__message">{{ t('app.update_message') }}</p>
        <div class="update-overlay__actions">
          <button class="update-overlay__button update-overlay__button--secondary" @click="skip">
            {{ t('app.update_later') }}
          </button>
          <button class="update-overlay__button update-overlay__button--primary" @click="reload">
            {{ t('app.update_now') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { applyAppUpdate } from '@/composables/appUpdate'

const { t } = useI18n()
const dismissed = ref(false)

function reload() {
  void applyAppUpdate(window.location.href)
}

// Dismissal lasts until the next in-app navigation, which applies the update
// on its own (see `useVersionCheck`).
function skip() {
  dismissed.value = true
}
</script>

<style scoped lang="scss">
.update-overlay {
  position: fixed;
  inset: 0;
  z-index: 8500;
  display: flex;
  align-items: center;
  justify-content: center;
}

.update-overlay__backdrop {
  position: absolute;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(2px);
}

.update-overlay__dialog {
  position: relative;
  z-index: 1;
  background-color: var(--cds-layer, #fff);
  border-radius: 8px;
  padding: 2rem;
  max-width: 420px;
  width: 90%;
  text-align: center;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
}

.update-overlay__title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--cds-text-primary, #161616);
  margin-bottom: 0.75rem;
}

.update-overlay__message {
  font-size: 0.875rem;
  color: var(--cds-text-secondary, #525252);
  margin-bottom: 1.5rem;
  line-height: 1.5;
}

.update-overlay__actions {
  display: flex;
  gap: 0.75rem;
  justify-content: center;
}

.update-overlay__button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 2.5rem;
  padding: 0 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.15s;

  &:focus-visible {
    outline: 2px solid var(--cds-focus, #0f62fe);
    outline-offset: 2px;
  }
}

.update-overlay__button--primary {
  color: #fff;
  background-color: var(--app-brand-color, #0f62fe);

  &:hover {
    background-color: var(--app-brand-color-hover, #0353e9);
  }
}

.update-overlay__button--secondary {
  color: var(--cds-text-primary, #161616);
  background-color: var(--cds-layer-hover, #e8e8e8);

  &:hover {
    background-color: var(--cds-layer-active, #d1d1d1);
  }
}
</style>
